import { ShopOutlined } from '@ant-design/icons';
import { Row, Select } from 'antd';
import { useGetAllCompanyGroupsQuery } from 'app/api';
import {
  selectIsConsultant,
  selectPermissions,
} from 'app/containers/Authorization/slice/selectors';
import { useCompanySlice } from 'app/containers/Company/slice';
import {
  selectConfirmedCompanies,
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { COMPANY_SELECT_DISABLED_ROUTES } from 'app/routes';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { TextRegular14 } from 'styles/typography';
import { IOption } from 'types';
import ShowIfDesktop from 'utils/responsiveQuery/ShowIfDesktop';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import CompanyGroupDrawer from '../CompanyGroupDrawer/CompanyGroupDrawer';

interface IProps {}

export const CompanySelect: FC<IProps> = () => {
  const dispatch = useDispatch();
  const { mobileOrTablet } = useResponsiveQuery();

  const { t } = useTranslation();

  const location = useLocation();

  const { actions: companyActions } = useCompanySlice();

  const { companyAdministration } = useSelector(selectPermissions);
  const companies = useSelector(selectConfirmedCompanies);
  const envId = useSelector(selectSelectedEnvId) as number;

  const selectedCompanyId = useSelector(selectSelectedCompanyId);
  const selectedCompanyGroupId = useSelector(selectSelectedCompanyGroupId);

  const isConsultant = useSelector(selectIsConsultant);

  const { data: companyGroups } = useGetAllCompanyGroupsQuery(
    { envId },
    { skip: !isConsultant },
  );

  const companyGroupOptions: IOption[] =
    companyGroups?.map(companyGroup => ({
      value: companyGroup.id,
      label: companyGroup.name,
    })) || [];

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isConsultantDrawerOpen, setIsConsultantDrawerOpen] = useState(false);

  const isCompanySelectVisible = useMemo(() => {
    let isVisibleCompany = true;
    COMPANY_SELECT_DISABLED_ROUTES.forEach(route => {
      if (matchPath(location.pathname, route)) {
        isVisibleCompany = false;
      }
    });
    return isVisibleCompany;
  }, [location.pathname]);

  const companyOptions: IOption[] = [
    {
      label: t('Header.AllCompanies', 'All'),
      value: 0,
    },
    ...(companies?.map(company => ({
      value: company.id,
      label: company.name,
    })) || []),
  ];

  const color = !companyAdministration ? 'rgba(0, 0, 0, 0.25)' : undefined;

  const onCompanySelect = (clickedCompanyId: number) => {
    dispatch(companyActions.setSelectedCompanyId(clickedCompanyId || null));
  };

  const onDropdownVisibleChange = (isOpen: boolean) => {
    if (isConsultant) {
      setIsConsultantDrawerOpen(true);
    } else {
      setIsSelectOpen(isOpen);
    }
  };

  if (!isCompanySelectVisible) {
    return null;
  }
  const commonCompanyAndCompanyGroupSelectProps = {
    style: { maxWidth: mobileOrTablet ? 100 : 'unset' },
    dropdownMatchSelectWidth: false,
    onDropdownVisibleChange,
    open: isSelectOpen,
    onSelect: onCompanySelect,
    bordered: false,
    disabled: !companyAdministration,
  };

  return (
    <>
      <Row align="middle" style={{ color }} wrap={false}>
        <ShopOutlined className="var-select-icon" />
        {selectedCompanyGroupId ? (
          <>
            <ShowIfDesktop>
              <TextRegular14 style={{ marginLeft: 5, color }}>
                {t('Common.CompanyGroup', 'Company group')}:
              </TextRegular14>
            </ShowIfDesktop>

            <Select
              {...commonCompanyAndCompanyGroupSelectProps}
              value={selectedCompanyGroupId}
              options={companyGroupOptions}
            />
          </>
        ) : (
          <>
            <ShowIfDesktop>
              <TextRegular14 style={{ marginLeft: 5, color }}>
                {t('Common.Company', 'Company')}:
              </TextRegular14>
            </ShowIfDesktop>

            <Select
              {...commonCompanyAndCompanyGroupSelectProps}
              value={selectedCompanyId || 0}
              options={companyOptions}
            />
          </>
        )}
      </Row>
      <CompanyGroupDrawer
        isOpen={isConsultantDrawerOpen}
        onClose={() => setIsConsultantDrawerOpen(false)}
      />
    </>
  );
};
